import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, Calendar, Truck, User } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { useUpdateAnomalieCarburant } from '@/hooks/useTransport';
import { extractError } from '@/api/client';
import {
  RATIO_GRAVITE_COLORS, STATUT_ANOMALIE_LABELS,
  type AnomalieCarburant, type StatutAnomalieCarburant,
} from '@/types';

interface Props {
  open:     boolean;
  onClose:  () => void;
  anomalie: AnomalieCarburant | null;
}

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtInt = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');

export const AnomalieDetailDialog = ({ open, onClose, anomalie }: Props) => {
  const update = useUpdateAnomalieCarburant();

  const [statut, setStatut]                       = useState<StatutAnomalieCarburant>('ouverte');
  const [assignedTo, setAssignedTo]               = useState('');
  const [commentaires, setCommentaires]           = useState('');
  const [decisionFinale, setDecisionFinale]       = useState('');
  const [error, setError]                         = useState<string | null>(null);

  useEffect(() => {
    if (!open || !anomalie) return;
    setStatut(anomalie.statut);
    setAssignedTo(anomalie.assignedToUserId ?? '');
    setCommentaires(anomalie.commentairesInvestigation ?? '');
    setDecisionFinale(anomalie.decisionFinale ?? '');
    setError(null);
  }, [open, anomalie]);

  if (!anomalie) return null;
  const bon = anomalie.bonCarburant;
  const ratioColor = anomalie.gravite === 'grave' ? RATIO_GRAVITE_COLORS.anomalie
                    : anomalie.gravite === 'modere' ? RATIO_GRAVITE_COLORS.eleve
                    : RATIO_GRAVITE_COLORS.faible;

  const requiresDecision = statut === 'resolue' || statut === 'classee';
  const canSubmit = statut !== anomalie.statut ||
    assignedTo !== (anomalie.assignedToUserId ?? '') ||
    commentaires !== (anomalie.commentairesInvestigation ?? '') ||
    decisionFinale !== (anomalie.decisionFinale ?? '');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (requiresDecision && !decisionFinale.trim()) {
      setError('Décision finale obligatoire pour clore l\'anomalie (résolue / classée)');
      return;
    }
    try {
      await update.mutateAsync({
        id: anomalie.id,
        payload: {
          statut,
          assignedToUserId:          assignedTo.trim() || undefined,
          commentairesInvestigation: commentaires.trim() || undefined,
          decisionFinale:            decisionFinale.trim() || undefined,
        },
      });
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Anomalie carburant${anomalie.estRecidive ? ' — RÉCIDIVE' : ''}`}
      subtitle={`Bon ${bon?.numero ?? '—'} · ratio ${parseFloat(anomalie.ratioConstate).toFixed(0)}% · ${anomalie.gravite.toUpperCase()}`}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        {/* Bandeau récidive */}
        {anomalie.estRecidive && (
          <div style={{
            padding: 10, background: '#dc2626', color: 'white', borderRadius: 'var(--r-sm)',
            fontSize: 12, fontWeight: 700,
          }}>
            ⚠️ RÉCIDIVE — 3 bons consécutifs &gt; 115% sur le même camion. Notification Directeur Pays requise.
          </div>
        )}

        {/* Détail du bon */}
        <Section title="Détail du bon">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
            <KV icon={<Calendar size={12} />}  label="Date du plein" value={bon ? fmtDateTime(bon.dateHeure) : '—'} />
            <KV icon={<Truck size={12} />}     label="Camion"        value={bon?.camion ? `${bon.camion.code}${bon.camion.immatriculation ? ` · ${bon.camion.immatriculation}` : ''}` : '—'} />
            <KV icon={<User size={12} />}      label="Chauffeur"     value={bon?.chauffeurNom ?? '—'} />
            <KV label="Mine / dépôt" value={bon?.mine ? `${bon.mine.code} · ${bon.mine.nom}` : '—'} />
            <KV label="Km compteur"  value={bon ? `${fmtInt(bon.kmCompteur)} km` : '—'} />
            <KV label="Km depuis dernier plein" value={bon?.kmDepuisDernierPlein ? `${fmtInt(bon.kmDepuisDernierPlein)} km` : '—'} />
            <KV label="Conso théorique" value={bon?.consoTheoriqueL ? `${parseFloat(bon.consoTheoriqueL).toFixed(1)} L` : '—'} />
            <KV label="Conso réelle"    value={bon?.consoReelleL ? `${parseFloat(bon.consoReelleL).toFixed(1)} L` : '—'} strong />
            <KV label="Ratio constaté"  value={`${parseFloat(anomalie.ratioConstate).toFixed(0)}%`} strong color={ratioColor} />
          </div>
        </Section>

        {/* Workflow */}
        <Section title="Workflow investigation">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Statut *">
              <select value={statut} onChange={(e) => setStatut(e.target.value as StatutAnomalieCarburant)} className="ds-fi" style={{ width: '100%' }}>
                {(Object.keys(STATUT_ANOMALIE_LABELS) as StatutAnomalieCarburant[]).map((s) => (
                  <option key={s} value={s}>{STATUT_ANOMALIE_LABELS[s]}</option>
                ))}
              </select>
            </Field>
            <Field label="Enquêteur assigné (UUID utilisateur)">
              <input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="UUID ou laisser vide" />
            </Field>
          </div>
          <Field label="Commentaires d'investigation">
            <textarea value={commentaires} onChange={(e) => setCommentaires(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }}
              placeholder="Hypothèses, constatations terrain, interrogatoire chauffeur, vérifications mécaniques..." />
          </Field>
          <Field label={`Décision finale${requiresDecision ? ' *' : ' (optionnel — requis pour clôturer)'}`}>
            <textarea value={decisionFinale} onChange={(e) => setDecisionFinale(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }}
              placeholder="Ex : Sanction chauffeur 3 jours suspension · Réparation fuite carter le 20/05 · Classé sans suite (anomalie mesure)" />
          </Field>
        </Section>

        {/* Métadonnées */}
        <div style={{ padding: 8, background: 'var(--surface)', borderRadius: 4, fontSize: 11, color: 'var(--tx-3)', display: 'flex', justifyContent: 'space-between' }}>
          <span>Créée le {fmtDateTime(anomalie.createdAt)}</span>
          {anomalie.resoluAt && <span>Résolue le {fmtDateTime(anomalie.resoluAt)}</span>}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || update.isPending}>
            {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }} className="space-y-2">
    <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 6px' }}>{title}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

const KV = ({ icon, label, value, strong, color }: { icon?: ReactNode; label: string; value: string; strong?: boolean; color?: string }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {icon}{label}
    </div>
    <div style={{
      fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 500,
      color: color ?? 'var(--tx-1)',
      fontVariantNumeric: 'tabular-nums', letterSpacing: 'normal',
    }}>{value}</div>
  </div>
);
